import React, { useState } from 'react'

export { ResourceRecordContext, useResourceRecordContext } from './ResourceRecordContextObject'
import { ResourceRecordContext } from './ResourceRecordContextObject'

import _ from 'lodash'
import { toast } from 'sonner'

import { useDataProviderContext } from './DataProviderContext'
import { confirm } from '@/shared/ui/dialog'
import { ResourceRecordForm } from '../components/ResourceRecordForm'
import { ResourceRecordFormWizard } from '../components/ResourceRecordFormWizard'
import { useRecord } from '../hooks/useRecord'
import { showErrorAlert } from '@/shared/lib/errors'


export const ResourceRecordProvider = ({
  apiBasePath,
  id,
  edit,
  options,
  forceIdPresence = true,
  shouldFetch = true,
  params,
  customAction,
  children,
  ...rest
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedRecord, setSavedRecord] = useState(null)
  const { dataProvider } = useDataProviderContext()
  const isEdit = edit || !!id || !forceIdPresence
  const {
    record: loadedRecord,
    errors,
    isLoading,
    isError,
    isSuccess,
    loadWrapperConfig,
    isEmpty,
    mutate,
    ...recordRest
  } = useRecord({
    r: apiBasePath,
    id,
    options: {
      revalidateOnFocus: false,
      ...options
    },
    forceIdPresence,
    shouldFetch,
    params,
    action: customAction
  })
  const record = savedRecord ? savedRecord : loadedRecord

  const handleDelete = async (props, config, path = apiBasePath, callback) => {
    let status
    let id = props
    if (_.isObject(props)) {
      id = props.id
      config = props.config
      path = props.path || apiBasePath
      callback = props.callback
    }

    if (!path)
      throw new Error(
        `No apiBasePath provided for delete action with id '${id}'`
      )

    let mustConfirm = true
    let title = 'Tem certeza?'
    let text = 'O registro será excluído permanentemente!'

    if (config) {
      const { confirm, confirmTitle, confirmText } = config

      if (confirm === false) mustConfirm = false
      if (confirmTitle) title = confirmTitle
      if (confirmText) text = confirmText
    }

    const deleteRecord = async id => {
      try {
        const deleteRes = await dataProvider.delete(path, id)

        toast.success('Excluído!', {
          description: 'Seu registro foi excluído permanentemente.'
        })

        status = 'deleted'
        callback && callback(true, _.get(deleteRes, 'data'))
      } catch (error) {
        status = 'error'
        showErrorAlert(error)

        callback && callback(false, error)
      }
    }

    if (mustConfirm) {
      try {
        const isConfirmed = await confirm({
          title,
          description: text,
          variant: 'warning',
          confirmLabel: 'Sim, pode excluir!',
          cancelLabel: 'Não',
          confirmVariant: 'destructive'
        })

        if (isConfirmed) {
          await deleteRecord(id)
        } else {
          status = 'canceled'
        }
      } catch (error) {}
    } else {
      await deleteRecord(id)
    }

    return { status }
  }

  const contextProps = {
    apiBasePath,
    id,
    record: savedRecord ? savedRecord : record,
    errors,
    isLoading,
    isError,
    isSuccess,
    isEmpty,
    loadWrapperConfig,
    isSubmitting,
    isEdit,
    isNew: !isEdit,
    customAction,
    dataProvider,
    ...recordRest,
    ...rest
  }
  const contextMethods = {
    handleDelete,
    setIsSubmitting,
    setSavedRecord,
    mutate
  }
  const contextComponents = {
    Form: ResourceRecordForm,
    FormWizard: ResourceRecordFormWizard
  }

  return (
    <ResourceRecordContext.Provider
      value={{
        ...contextProps,
        ...contextMethods,
        ...contextComponents
      }}
    >
      {children}
    </ResourceRecordContext.Provider>
  )
}

export const withResourceRecord = (Component, propConfig) => {
  const WrappedComponent = props => {
    const config = _.isFunction(propConfig) ? propConfig(props) : propConfig
    return (
      <ResourceRecordProvider {...props} {...config} config={config}>
        <Component config={config} {...props} />
      </ResourceRecordProvider>
    )
  }

  WrappedComponent.displayName = `withResourceRecord(${
    Component.displayName || Component.name
  })`

  return WrappedComponent
}
